import { useEffect, useRef, useState } from 'react';

interface Options {
  /** Wait this long before showing the skeleton, so fast responses skip it. */
  showDelay?: number;
  /** Once shown, keep the skeleton up at least this long, to avoid a flash. */
  minVisible?: number;
}

/**
 * Debounced loading flag for skeletons: only turns true after `showDelay` of
 * sustained loading (fast fetches never flash a skeleton), and once true stays
 * true for at least `minVisible` even after loading ends (no flash-and-gone).
 */
export function useDelayedLoading(
  isLoading: boolean,
  { showDelay = 200, minVisible = 400 }: Options = {},
): boolean {
  const [visible, setVisible] = useState(false);
  const shownAt = useRef<number | null>(null);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    if (isLoading) {
      // Delay showing so quick responses resolve before any skeleton appears.
      timer = setTimeout(() => {
        shownAt.current = Date.now();
        setVisible(true);
      }, showDelay);
    } else if (shownAt.current !== null) {
      // Skeleton is up: hold it for the remainder of the minimum visible window.
      const remaining = Math.max(0, minVisible - (Date.now() - shownAt.current));
      timer = setTimeout(() => {
        shownAt.current = null;
        setVisible(false);
      }, remaining);
    }

    return () => {
      if (timer !== undefined) {
        clearTimeout(timer);
      }
    };
  }, [isLoading, showDelay, minVisible]);

  return visible;
}
